"use client";

import React, { useState } from "react";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { AnimatePresence, Variants, motion } from "framer-motion";
import { cn } from "@/src/lib/utils";
import { CATEGORY_SUB_MENU } from "@/src/Config";
import { Heading } from "../common/Heading";
import { Paragraph } from "../common/Paragraph";
import Link from "next/link";
import { Button, buttonVariants } from "../ui/button";
import { Utility } from "@/src/utils/utility";
import { usePathname } from "next/navigation";

export const variants: Variants = {
  "in-view": { x: "0px", transition: { type: "tween", ease: "easeOut" } },
  "out-of-view": (index: number) => ({
    x: index > 0 ? "250px" : "-550px",
    transition: { type: "tween", ease: "easeOut" },
  }),
};

const navigationData = CATEGORY_SUB_MENU;

//DEV
//https://dev.to/ksaini9/creating-a-multi-level-sidebar-animation-with-tailwind-and-framer-motion-in-react-53f1

function Sidebar({
  category,
  setIsOpen,
}: {
  category: any;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const pathname = usePathname();

  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [mainCategory, setMainCategory] = useState<any>(null);
  const [initialSLug, setInitialSLug] = useState<any>(null);

  const goToNextLevel = (item: any, _initialSLug: string) => {
    // console.log(item?.children)
    if (!item.children || item?.children?.length <= 0) {
      return;
    }
    // Append current item and its children to selectedItems
    setSelectedItems((prevSelectedItems) => [...item?.children]);
    setMainCategory(item);
    setInitialSLug(_initialSLug);
  };

  const goBack = () => {
    // setSelectedItems((prevSelectedItems) => prevSelectedItems.slice(0, -1));
    setSelectedItems([]);
  };

  return (
    <div className="relative flex h-full flex-col overflow-y-auto text-white">
      <div className="relative mt-6">
        <AnimatePresence>
          <motion.ul
            // variants={variants}
            initial={{
              x: "100%",
            }}
            // animate={selectedItems.length > 0 ? "out-of-view" : "in-view"}
            animate={{
              x: selectedItems.length > 0 ? "-100%" : "0px",
              transition: { type: "tween", ease: "easeOut" },
            }}
            custom={selectedItems.length > 0 ? -1 : 0}
            className="absolute left-0 top-0 w-full duration-200"
          >
            {/* First level items */}
            {category?.map((item: any, i: number) => {
              return (
                <AnimatePresence mode="wait" key={i}>
                  {selectedItems.length === 0 && (
                    <motion.div
                      key={i}
                      initial={{ opacity: 1, height: "auto" }} // Start fully visible
                      animate={{ opacity: 1, height: "auto" }} // Stay visible
                      exit={{ opacity: 0, height: "auto" }} // Smoothly hide
                      transition={{ duration: 0.3, ease: "easeInOut" }} // Control timing
                      className="overflow-hidden"
                    >
                      <SelectMainItem
                        i={i}
                        item={item}
                        goToNextLevel={goToNextLevel}
                        setIsOpen={setIsOpen}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              );
            })}
          </motion.ul>
        </AnimatePresence>

        {/* Subsequent levels */}
        <AnimatePresence mode="wait">
          {
            selectedItems.length > 0 && (
              <motion.ul
                initial={{ x: "100%" }} // Start from the right (off-screen)
                animate={{
                  x: "0px", // Move to the final position (on-screen)
                  transition: { type: "tween", ease: "easeOut", duration: 0.3 },
                }}
                exit={{
                  x: "200%", // Move off-screen to the left when exiting
                  transition: { type: "tween", ease: "easeOut", duration: 0.3 },
                }}
                // custom={index + 1 === selectedItems.length ? 1 : -1}
                className="absolute left-0 top-0 mx-4 w-full duration-200"
              >
                <li className="item-center flex w-fit justify-between pb-2">
                  <Button
                    className="flex items-center"
                    onClick={goBack}
                    variant={"outline"}
                  >
                    <ChevronLeft
                      style={{
                        fontSize: 30,
                        color: "black",
                      }}
                    />{" "}
                    <Paragraph as="div" className="pl-2">
                      Back
                    </Paragraph>
                  </Button>

                  <Link
                    // href={`/${initialSLug}/${mainCategory?.slug}`}
                    href={Utility.getRouteLinkUrl({
                      main: `/${initialSLug}/${mainCategory?.slug}`,
                      blog: `/blog/${initialSLug}/${mainCategory?.slug}`,
                      pathname: pathname,
                    })}
                    className={cn(
                      buttonVariants({ variant: "link" }),
                      "text-wrap capitalize",
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="text-sm font-medium underline hover:text-main-600">
                      {" "}
                      {`See all ${mainCategory.name}`}
                    </div>
                  </Link>
                </li>

                {/* Dynamically render the items based on the current level */}
                {selectedItems
                  // .slice(0, index + 1)
                  .map((item: any, i: number) => {
                    return (
                      <li key={i} className="flex flex-col gap-4 px-4 py-2">
                        <div className="cursor-pointer hover:underline">
                          <Heading>{item?.name}</Heading>
                        </div>

                        <div className="ml-2 flex flex-col gap-2">
                          {item?.children?.length > 0 &&
                            item?.children?.map((subItem: any, i: number) => (
                              <Link
                                // href={`/${initialSLug}/${mainCategory?.slug}/${item?.slug}/${subItem?.slug}`}
                                href={Utility.getRouteLinkUrl({
                                  main: `/${initialSLug}/${mainCategory?.slug}/${item?.slug}/${subItem?.slug}`,
                                  blog: `/blog/${initialSLug}/${mainCategory?.slug}/${item?.slug}/${subItem?.slug}`,
                                  pathname: pathname,
                                })}
                                key={i}
                                onClick={() => setIsOpen(false)}
                                className={cn(
                                  buttonVariants({ variant: "ghost" }),
                                  "flex w-fit justify-start text-wrap",
                                )}
                              >
                                <Paragraph
                                  as="div"
                                  className={cn("text-sm hover:text-main-600")}
                                >
                                  {subItem?.name}
                                </Paragraph>
                              </Link>
                            ))}
                          <Link
                            // href={`/${initialSLug}/${mainCategory?.slug}/${item?.slug}`}
                            href={Utility.getRouteLinkUrl({
                              main: `/${initialSLug}/${mainCategory?.slug}/${item?.slug}`,
                              blog: `/blog/${initialSLug}/${mainCategory?.slug}/${item?.slug}`,
                              pathname: pathname,
                            })}
                            className={cn(
                              buttonVariants({ variant: "link" }),
                              "flex justify-start text-wrap text-sm font-medium capitalize underline hover:text-main-600",
                            )}
                            onClick={() => setIsOpen(false)}
                          >
                            {" "}
                            {`See all ${item.name}`}
                          </Link>
                        </div>
                      </li>
                    );
                  })}
              </motion.ul>
            )
            //   );
            // })
          }
        </AnimatePresence>
      </div>
    </div>
    // </motion.div >
    // {/* </AnimatePresence> */ }
  );
}

// const SelectMainItem = ({ i, item, goToNextLevel, setIsOpen }: {
//   i: number,
//   item: any,
//   goToNextLevel: (item: any) => void,
//   setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
// }) => {
//   return <li key={i}>
//     {(item.children ?? []).length > 0 ? (
//       <Button
//         variant={"ghost"}
//         className="w-full flex text-gray-800 justify-between p-7"
//         onClick={() => goToNextLevel(item)}
//       >
//         <span >{item.name}</span>
//         {(item.children ?? []).length > 0 && (
//           <ChevronRight />
//         )}
//       </Button>
//     ) : (
//       <Link
//         href={`${item?.slug}`}
//         onClick={() => setIsOpen(false)}
//         className={cn(buttonVariants({ variant: "ghost" }), "w-full flex justify-start text-gray-800 p-7")}
//       >
//         <span >{item.name}</span>
//       </Link>
//     )}
//   </li>
// }

const SelectMainItem = ({
  i,
  item,
  goToNextLevel,
  setIsOpen,
  hideDropDownIcon,
}: {
  i: number;
  item: any;
  goToNextLevel: (item: any, initialSlug: string) => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hideDropDownIcon?: boolean;
}) => {
  const [isOpen, setIsOpenState] = useState(false); // Track the open/close state of the dropdown

  const pathname = usePathname();

  const toggleDropdown = () => {
    setIsOpenState((prev) => !prev);
  };

  return (
    <li key={i}>
      {(item.children ?? []).length > 0 ? (
        <Button
          variant={"ghost"}
          className="flex w-full justify-between p-7 text-gray-800"
          onClick={() => {
            toggleDropdown(); // Toggle dropdown open/close
          }}
        >
          <Link
            // href={`${item?.slug}`}
            href={Utility.getRouteLinkUrl({
              main: `/${item?.slug}`,
              blog: `/blog/${item?.slug}`,
              pathname: pathname,
            })}
          >
            {item.name}
          </Link>
          {!hideDropDownIcon && (
            <span>{isOpen ? <ChevronUp /> : <ChevronDown />}</span>
          )}
        </Button>
      ) : (
        <Link
          // href={`${item?.slug}`}
          href={Utility.getRouteLinkUrl({
            main: `/${item?.slug}`,
            blog: `/blog/${item?.slug}`,
            pathname: pathname,
          })}
          onClick={() => setIsOpen(false)}
          className={cn(
            buttonVariants({ variant: "ghost" }),
            "flex w-full justify-start text-wrap p-7 text-start text-gray-800",
          )}
        >
          <span>{item.name}</span>
        </Link>
      )}

      {/* Dropdown content animation using Framer Motion */}
      {isOpen && (item.children ?? []).length > 0 && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="ml-6" // Indent the dropdown content
        >
          <ul>
            {item.children?.map((child: any, idx: number) => (
              // <SelectMainItem
              //   key={idx}
              //   i={idx}
              //   item={child}
              //   goToNextLevel={goToNextLevel}
              //   setIsOpen={setIsOpen}
              //   hideDropDownIcon={true}
              // />

              <Button
                key={idx}
                variant={"ghost"}
                className="flex w-full justify-between p-7 text-gray-800"
                onClick={() => {
                  goToNextLevel(child, item?.slug);
                  toggleDropdown(); // Toggle dropdown open/close
                }}
              >
                <span>{child.name}</span>
              </Button>
            ))}
          </ul>
        </motion.div>
      )}
    </li>
  );
};

export default Sidebar;
